import React, {useState} from "react";
import {useDispatch} from "react-redux";
import {Box} from "@mui/system";
import {RadioGroup} from "@mui/material";
import _ from "lodash";
import {processCannotResolveOptions} from "_helpers";
import CannotResolveOption from "./CannotResolveOption";
import ActionButton from "../../../../../common/ActionButton";
import cannotResolveOptions from "./CannotResolveOptions";
import {buildCannotResolvePayload} from "../../../Utilities/VoteUtils";
import {submitReason} from "./CannotResolveUtils";
import {cannotResolveOptionType} from "_constants";
import InternalNoteEntry from "./InternalNote/InternalNoteEntry";
import CustomFieldEntry from "../../BodySection/Response/CustomField/CustomFieldEntry";
import useViewCustomFields from "../../BodySection/Response/CustomField/hooks/useViewCustomFields";
import {
    isCustomFieldSupported,
    validateCustomFieldsInput
} from "../../BodySection/Response/CustomField/CustomFieldUtils";
import {SnackBar} from "../../../../../../common";

export default function CannotResolveContainer({message, handleClose, handleOptionSubmitted, adminOptionSubmitted}) {
    const [optionType, setOptionType] = useState('');
    const messageHandleType = _.get(message, 'handle.type.name') || _.get(message, 'dataSourceHandle.type');
    const options = processCannotResolveOptions(cannotResolveOptions, messageHandleType);
    const dispatch = useDispatch();
    const [internalNoteSelected, setInternalNoteSelected] = useState(false);
    const [escalateSelected, setEscalateSelected] = useState(false);
    const [responseNeededSelected, setNoResponseNeededSelected] = useState(false);
    const [customFieldsInput, setCustomFieldsInput] = useState([]);
    const { customFields, loadingCustomFields } = useViewCustomFields();
    const shouldAddCustomFields = !loadingCustomFields && (escalateSelected || responseNeededSelected) && isCustomFieldSupported(_.toString(message?.sourceName).toLowerCase()) && customFields.length > 0;

    const handleChange = (event) => {
        setOptionType(_.get(event, 'target.value'));
    }

    const handleSubmit = () => {
        if(_.isEmpty(optionType)) return;
        const isValidCustomFields = validateCustomFieldsInput(customFields, customFieldsInput, shouldAddCustomFields);
        if(!isValidCustomFields?.isValid) {
            SnackBar.showMessage(isValidCustomFields?.message);
            return;
        }
        const payload = buildCannotResolvePayload(message, optionType, {customFields: customFieldsInput});
        submitReason(dispatch, payload, optionType);
        handleOptionSubmitted(true);
    }

    const handleCustomFields = (customFields) => {
        setCustomFieldsInput(customFields);
    };

    const handleOptionChange = (event) => {
        const defaultValue = _.get(event, 'target.defaultValue');
        handleInternalNote(defaultValue);
        handleEscalateSelection(defaultValue);
        handleNoResponseNeededSelection(defaultValue);
    }

    const handleInternalNote = (selectedOption) => {
        const isInternalNote = selectedOption === cannotResolveOptionType.INTERNAL_NOTE;
        setInternalNoteSelected(isInternalNote);
    }

    const handleEscalateSelection = (selectedOption) => {
        const isEscalate = selectedOption === cannotResolveOptionType.ESCALATE;
        setEscalateSelected(isEscalate);
    }

    const handleNoResponseNeededSelection = (selectedOption) => {
        const isNoResponseNeeded = selectedOption === cannotResolveOptionType.NO_RESPONSE_NEEDED;
        setNoResponseNeededSelected(isNoResponseNeeded);
    }

    const Content = ({options}) => {
        return options.map((option, index) => {
            return(
                <Box key={index}>
                    <CannotResolveOption option={option} onChange={handleOptionChange} />
                </Box>
            )
        });
    }

    const ActionButtonOption = ({isInternalNote}) => {
        if(isInternalNote) return <InternalNoteEntry handleOptionSubmitted={handleOptionSubmitted} message={message} />
        return (
            <ActionButton
                label={'Submit reason'}
                action={handleSubmit}
                disabled={adminOptionSubmitted}
                withIcon={false}
                sx={style.submitReason}
            />
        )
    }

    return(
        <Box sx={{marginTop: '-8px'}}>
            <Box sx={style.heading}>Select a reason that this message cannot be resolved:</Box>
            <RadioGroup name="cannot-resolve-options-group" onChange={handleChange}>
                <Content options={options} />
            </RadioGroup>
            <Box sx={style.buttonContainer}>
                <ActionButton
                    label={'Cancel'}
                    action={handleClose}
                    buttonBordered
                    useBorderColor={'#003300'}
                    withIcon={false}
                    sx={style.cancel}
                />
                {
                    shouldAddCustomFields &&
                    <Box sx={{mx: '15px'}}>
                        <CustomFieldEntry
                            msgDataSourceId={message?.msgDataSource?.id}
                            customFields={customFields}
                            customFieldsInput={customFieldsInput}
                            handleCustomFields={handleCustomFields}
                        />
                    </Box>
                }
                <ActionButtonOption isInternalNote={internalNoteSelected} />
            </Box>
        </Box>
    )
}

const style = {
    heading: {
        fontFamily: 'LarsseitRegular',
        fontSize: '16px',
        color: '#0C2728',
        letterSpacing: '-0.01em',
        marginBottom: '12px'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '27px'
    },
    cancel: {
        height: '35px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: '8px'
    },
    submitReason: {
        height: '35px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}